/**
 * Reads a component's props from its container, in priority order:
 *
 * 1. `_sdxTreeProps` expando - attached in memory by the owning
 *    framework scaffold (MountPointScaffold's props-attach effect)
 *    from the parent envelope's ComponentNode tree. Freshest source:
 *    it is rewritten by the live framework commit on SPA navigation
 *    and prop updates, where a DOM-serialized copy would be stale.
 * 2. `data-props` attribute.
 * 3. `<script type="application/json" data-sdx-props-for>` as a
 *    child or adjacent sibling - the Twig placeholder contract, and
 *    the only channel on non-DRAST themes and html-only output.
 *    Checked before the marker below so cached pages rendered by an
 *    older scaffold (script still in the HTML) keep mounting.
 * 4. `data-sdx-tree-props` marker with no expando yet: the scaffold
 *    promises in-memory delivery but has not committed - return
 *    PROPS_PENDING so the coordinator defers instead of mounting
 *    with empty props. The scaffold nudges attachBehaviors after
 *    attaching.
 * 5. No source at all: `{}` (registration mounts carry no props).
 */

/**
 * Sentinel: the container's props arrive in memory from the owning
 * scaffold's commit effect and have not been attached yet.
 */
export const PROPS_PENDING: unique symbol = Symbol('sdx.propsPending');

interface PropsHost extends HTMLElement {
  _sdxTreeProps?: Record<string, unknown>;
}

export function readPropsFromContainer(
  container: HTMLElement,
  componentId: string,
  logPrefix: string,
): unknown {
  const inMemory = (container as PropsHost)._sdxTreeProps;
  if (inMemory !== undefined) {
    return inMemory;
  }

  let source = container.dataset.props as string | undefined;
  if (source === undefined) {
    const selector = `script[type="application/json"][data-sdx-props-for="${componentId}"]`;
    const childScript = container.querySelector<HTMLScriptElement>(selector);
    if (childScript) {
      source = childScript.textContent ?? undefined;
    }
    else {
      const next = container.nextElementSibling;
      const prev = container.previousElementSibling;
      const sibling = (next && next.matches(selector))
        ? next
        : (prev && prev.matches(selector) ? prev : null);
      if (sibling) {
        source = (sibling as HTMLScriptElement).textContent ?? undefined;
      }
    }
  }
  if (source === undefined && container.dataset.sdxTreeProps !== undefined) {
    return PROPS_PENDING;
  }
  try {
    return JSON.parse(source ?? '{}');
  }
  catch {
    console.error(`${logPrefix} Invalid props JSON for ${componentId}`);
    return null;
  }
}
